import type {
  candidatosSchema,
  expedienteAConciliarSchema,
} from "@conciliacion/api";
import type { z } from "zod";

// ===========================================================================
// Lo que comparten las tres piezas de «Conciliar expediente».
//
// Los tipos salen del contrato y no se vuelven a escribir a mano: una copia
// del tipo es una copia que deja de compilar cuando cambia el contrato — o
// peor, que sigue compilando y deja de coincidir.
// ===========================================================================

export type Expediente = z.infer<typeof expedienteAConciliarSchema>;
export type Voucher = Expediente["vouchers"][number];
export type Boleta = Expediente["boletas"][number];
export type Candidatos = z.infer<typeof candidatosSchema>;
export type Candidato = Candidatos["filas"][number];

/**
 * Qué identifica a un pago en esta pantalla: la foto Y cuál de sus pagos.
 *
 * Una imagen puede traer dos vouchers —la boleta B001-122 tiene dos depósitos
 * de S/ 500 en la misma foto, y juntos la cubren— así que el recorte a secas
 * no alcanza para elegir ni para atar. Es la misma clave que usa
 * `enlace_pago` en la base.
 */
export const claveDe = (v: { recorte: string; pagoN: number }): string =>
  `${v.recorte}|${v.pagoN}`;

export const RAYA = "—";

export function dinero(
  centimos: number | null,
  moneda: "PEN" | "USD" | null = "PEN",
): string {
  if (centimos === null) return RAYA;
  const signo = moneda === "USD" ? "US$" : "S/";
  return `${signo} ${(centimos / 100).toLocaleString("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** `2026-01-22` como `22-01-2026`, que es como lo lee contabilidad. */
export function fecha(iso: string | null): string {
  if (!iso) return RAYA;
  const [a, m, d] = iso.split("-");
  return d && m && a ? `${d}-${m}-${a}` : iso;
}

/** `2026-01-22` como `22-01`, para cuando el año se sobreentiende. */
export function diaMes(iso: string | null): string {
  if (!iso) return RAYA;
  const [, m, d] = iso.split("-");
  return d && m ? `${d}-${m}` : iso;
}

/** Los últimos cuatro dígitos de una cuenta, que es como se la nombra. */
export function cola(numero: string | null): string {
  if (!numero) return RAYA;
  const digitos = numero.replace(/\D/g, "");
  return digitos.length > 4 ? `····${digitos.slice(-4)}` : numero;
}

/**
 * Qué hay que hacer con este voucher, en una palabra.
 *
 * Los tres estados se ven distintos a propósito: «atado», «sin abono» y «sin
 * atar» son tres situaciones y no dos. Marcar que un pago no está en ningún
 * extracto ES trabajo hecho, y pintarlo como pendiente haría que alguien lo
 * volviera a mirar cada vez que abre el expediente.
 */
export function comoVaElVoucher(v: Voucher): {
  etiqueta: string;
  tono: "ok" | "warn" | "neutro";
} {
  if (v.enlace) return { etiqueta: "Atado", tono: "ok" };
  if (v.sinAbono) return { etiqueta: "Sin abono", tono: "neutro" };
  return { etiqueta: "Sin atar", tono: "warn" };
}

/** El color de cada banco, solo como punto. Nunca informa solo. */
export function colorDeBanco(banco: string | null): string {
  const b = (banco ?? "").toUpperCase();
  if (b.includes("BCP")) return "#f26f21";
  if (b.includes("BBVA")) return "#1464a5";
  if (b.includes("INTERBANK")) return "#0b7a48";
  if (b.includes("ALFIN")) return "#7a3fa0";
  if (b.includes("YAPE")) return "#742284";
  if (b.includes("PLIN")) return "#0fb5ce";
  return "#94a3b8";
}
